<template>
  <div class="flex flex-col w-full" :class="{ 'border-none': !showBorder }">
    <div
      v-if="showNegative"
      class="px-1 py-2 flex items-center justify-between"
    >
      <div class="font-bold flex gap-1">
        <VanButton
          :class="{
            'btn-primary': isPositive,
            'btn-secondary': !isPositive,
          }"
          class="btn btn-xs rounded"
          @click="onPositiveClick(true)"
        >
          {{ t(`trait.positive`) }}
        </VanButton>
        <VanButton
          :class="{
            'btn-primary': !isPositive,
            'btn-secondary': isPositive,
          }"
          class="btn btn-xs rounded"
          @click="onPositiveClick(false)"
        >
          {{ t(`trait.negative`) }}
        </VanButton>
      </div>
      <div class="flex gap-2">
        <VanButton
          v-if="showFeatureParse"
          class="btn btn-xs rounded"
          @click="$router.push(`/generation/trait/extraction`)"
        >
          <i class="iconify mingcute--pic-line-import font-normal"></i>
          {{ t(`trait.feature.extraction`) }}
        </VanButton>
      </div>
    </div>
    <div class="border rounded pb-2 pr-2">
      <lay-tag-input
        :id="tagInputId"
        v-model="data1"
        :inputValue="inputValue1"
        class="lay-tag-input border-none max-h-[90px]"
        @blur="addInputTag(inputValue1, true)"
        @click="clickTag"
        @remove="removeTag"
        @update:input-value="onInputTagText"
        @press-enter="addInputTag"
      />
      <div v-if="showUpgradePrompt" class="flex justify-end">
        <button class="btn btn-xs rounded" @click="generatePrompt">
          <i class="iconify mingcute--magic-2-line"></i>
          {{ $t(`painting.promptUpdate.title`) }}
        </button>
        <button class="btn btn-xs rounded" @click="clickClearTag">
          <i class="iconify mingcute--delete-2-line"></i>
          {{ $t(`Buttons.clear`) }}
        </button>
      </div>
    </div>
  </div>

  <VanPopup
    v-model:show="showTagActions"
    class="py-4 flex flex-col gap-4"
    position="bottom"
    safe-area-inset-top
    round
  >
    <template v-if="toOperateTag">
      <div class="text-center">
        {{ toOperateTag!.displayText || toOperateTag.label }}
      </div>
      <VanCellGroup inset>
        <VanSwipeCell
          v-if="toOperateTag!.weight"
          class="rounded-lg overflow-hidden"
        >
          <VanCell
            :border="false"
            :label="$t('trait.tagAction.labelWeight')"
            :title="$t('trait.tagAction.weight')"
            center
          >
            <VanStepper
              v-model="toOperateTag!.weight"
              :decimal-length="1"
              max="2"
              min="0.1"
              step="0.1"
            />
          </VanCell>
          <template #right>
            <VanButton
              :text="$t('Buttons.delete')"
              class="h-full bg-error border-error rounded-l-none"
              @click="toOperateTag!.weight = undefined"
            />
          </template>
        </VanSwipeCell>
        <VanCell
          v-else
          :title="$t('trait.setWeight')"
          is-link
          @click="toOperateTag!.weight = '1.0'"
        />
      </VanCellGroup>
      <VanCellGroup inset>
        <VanCell :title="$t('trait.delete')" clickable @click="onRemoveTag" />
      </VanCellGroup>

      <VanCellGroup inset>
        <VanCell
          :title="$t('Buttons.confirm')"
          class="text-primary"
          clickable
          @click="confirmPromptWeight"
        />
        <VanCell
          :title="$t('Buttons.cancel')"
          clickable
          @click="showTagActions = false"
        />
      </VanCellGroup>
    </template>
  </VanPopup>

  <VanPopup
    v-model:show="showPromptModal"
    class="py-4 flex flex-col gap-4"
    round
  >
    <div class="p-6 rounded-lg shadow-lg max-w-md mx-auto text-base-content/50">
      <h1 class="text-lg font-semibold mb-4">
        {{ t(`painting.promptUpdate.confirmReplace`) }}
      </h1>

      <h2 class="text-base font-medium mb-2">
        {{ $t(`painting.promptUpdate.origin`) }}
      </h2>
      <p class="mb-4 h-[60px] overflow-auto">
        {{ isPositive ? prompt : negativePrompt }}
      </p>

      <h2 class="text-base font-medium mb-2 flex items-center">
        {{ $t(`painting.promptUpdate.new`) }}
        <i
          class="iconfont icon-rotate-right ml-2 text-primary"
          @click="generatePrompt"
        />
      </h2>
      <p class="mb-4 h-[60px] overflow-auto">
        {{ newPrompt }}
      </p>

      <div class="flex justify-center items-center gap-4 mt-4">
        <button class="btn btn-sm" @click="showPromptModal = false">
          {{ $t(`Buttons.cancel`) }}
        </button>
        <button
          class="btn btn-primary btn-sm"
          @click="
            emit(
              isPositive ? 'update:prompt' : 'update:negativePrompt',
              newPrompt
            ),
              (showPromptModal = false)
          "
        >
          {{ $t(`Buttons.confirm`) }}
        </button>
      </div>
    </div>
  </VanPopup>
</template>

<script lang="ts" setup>
import { ApiPostRefineText } from "@/apis/prompt";
import { ImagePromptItem } from "@/stores/prompts";
import { showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { watch } from "vue";
import { onMounted } from "vue";
import { nextTick } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    tagInputId?: string;
    showFeatureParse?: boolean;
    showNegative?: boolean;
    negativePrompt?: string;
    isPositive?: boolean;
    prompt: string;
    showUpgradePrompt?: boolean;
    showBorder?: boolean;
  }>(),
  {
    showFeatureParse: false,
    tagInputId: () => Math.random().toString(36).substring(7),
    showNegative: true,
    isPositive: true,
    showUpgradePrompt: true,
    showBorder: true,
  }
);

const emit = defineEmits([
  "update:prompt",
  "update:negativePrompt",
  "update:isPositive",
]);

const data1 = ref<ImagePromptItem[]>([]);

const inputValue1 = ref("");

const setPromptInput = () => {
  let text = props.isPositive ? props.prompt : props.negativePrompt!;
  data1.value = [];
  text.split(",").forEach((item) => {
    item.split("，").forEach((item) => {
      addInputTag(item, null, false);
    });
  });
  autoAddDataSetIdx();
};
const onPositiveClick = (_isPositive: boolean) => {
  if (_isPositive === props.isPositive) return;
  emit("update:isPositive", _isPositive);
  nextTick(setPromptInput);
};

const onInputTagText = (text: string) => {
  if (text.includes(",")) {
    text
      .split(",")
      .filter((item) => item.trim() !== "")
      .forEach((item) => addInputTag(item));
  } else if (text.includes("，")) {
    text
      .split("，")
      .filter((item) => item.trim() !== "")
      .forEach((item) => addInputTag(item));
  } else {
    inputValue1.value = text;
  }
};

const addInputTag = (_label: string, e?: any, needUpdate = true) => {
  _label = _label.trim();
  if (_label === "") return;
  if (e && typeof e !== "boolean") data1.value = data1.value.slice(0, -1);
  const [label, weight] = _label.split(":");

  let item: any = { label, text: label };
  if (weight) {
    item.weight = weight;
    item.label = label + ":" + weight;
  }
  item.value = item;
  data1.value.push(item);
  clearInputValue();
  if (needUpdate) {
    updatePromptToParent();
    autoAddDataSetIdx();
  }
};

const clickClearTag = () => {
  showConfirmDialog({
    message: t(`Message.clearPromptMessage`),
    confirmButtonText: t(`Buttons.confirm`),
    cancelButtonText: t(`Buttons.cancel`),
  }).then(() => {
    data1.value = [];
    updatePromptToParent();
  });
};

const clearInputValue = () => {
  document
    .getElementById(props.tagInputId)!
    .getElementsByTagName("input")[0].value = "";
  inputValue1.value = "";
};

const updatePromptToParent = () => {
  if (props.isPositive) {
    emit("update:prompt", data1.value.map((item) => item.label).join(","));
  } else {
    emit(
      "update:negativePrompt",
      data1.value.map((item) => item.label).join(",")
    );
  }
};

const autoAddDataSetIdx = () => {
  nextTick(() => {
    const listDOM = document
      .getElementById(props.tagInputId)!
      .getElementsByClassName("layui-tag-input-inner")[0];

    for (let i = 0; i < listDOM.children.length; i++) {
      //@ts-ignore
      listDOM.children[i].dataset.idx = i;
    }
    listDOM.scrollTop = listDOM.scrollHeight;
  });
};

// tag
const showTagActions = ref(false);
const toOperateTag = ref<ImagePromptItem | null>(null);
let toOperateTagData: any = null;
let toOperateTagIdx: any = null;
const clickTag = (e: any) => {
  const idx = e.target.dataset.idx;
  let inputDOM = document
    .getElementById(props.tagInputId)!
    .getElementsByTagName("input")[0];
  if (!idx || !data1.value[idx]) {
    // inputDOM.readOnly = false;
    return;
  }
  // inputDOM.readOnly = true;
  inputDOM.blur();
  onTagClick(data1.value[idx], idx);
};

const onTagClick = (tag: ImagePromptItem, idx: number) => {
  toOperateTag.value = { ...tag };
  toOperateTagData = tag;
  toOperateTagIdx = idx;
  showTagActions.value = true;
};

const removeTag = (tag: any) => {
  data1.value.push(tag);
};

const confirmPromptWeight = () => {
  toOperateTagData.weight = toOperateTag.value?.weight || "1.0";
  toOperateTagData.label =
    toOperateTagData.text + ":" + toOperateTagData.weight;
  showTagActions.value = false;
  updatePromptToParent();
};

const onRemoveTag = () => {
  data1.value.splice(toOperateTagIdx, 1);
  showTagActions.value = false;
  updatePromptToParent();
};

watch(
  () => props.prompt,
  (val, oldVal) => {
    if (oldVal === val) return;
    setPromptInput();
  }
);
watch(
  () => props.negativePrompt,
  (val, oldVal) => {
    if (oldVal === val) return;
    setPromptInput();
  }
);

// 细化提示词
const showPromptModal = ref(false);
const newPrompt = ref("");
const generatePrompt = async () => {
  let text = props.isPositive ? props.prompt : props.negativePrompt!;
  if (!text) {
    return showFailToast({ message: t(`Message.promptNotBeenFilledYet`) });
  }
  const loading = showLoadingToast(t(`Message.loading`));
  const res = await ApiPostRefineText(text);
  newPrompt.value = res.data.text;
  loading.close();
  if (!showPromptModal.value) {
    showPromptModal.value = true;
  }
};

onMounted(() => {
  nextTick(setPromptInput);
});
</script>

<style scoped>
:deep(.lay-tag-input span) {
  color: #000;
}

:deep(.lay-tag-input input) {
  background-color: var(--color-primary);
}

:deep(.layui-tag-input-inner) {
  overflow: auto;
  display: flex;
  flex-wrap: wrap;
}

:deep(.layui-tag-input-inner-input) {
  flex: 1;
}

:deep(.layui-tag-text) {
  pointer-events: none;
}

:deep(.layui-tag-bordered) {
  border-color: hsl(var(--p) / calc(var(--tw-border-opacity) * 0.4));
}
</style>
